(() => {
  const init = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    const button = root.querySelector('.theme-switch');
    const saved = localStorage.getItem('chromatica-theme');
    const systemDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    const setTheme = (theme) => {
      root.dataset.theme = theme;
      document.documentElement.dataset.chromaticaTheme = theme;
      document.body?.setAttribute('data-chromatica-theme', theme);
      button?.setAttribute('aria-pressed', String(theme === 'dark'));
      button?.setAttribute('aria-label', theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
    };
    setTheme(saved === 'dark' || saved === 'light' ? saved : (systemDark ? 'dark' : 'light'));
    button?.addEventListener('click', () => {
      const next = root.dataset.theme === 'dark' ? 'light' : 'dark';
      setTheme(next);
      localStorage.setItem('chromatica-theme', next);
    });

    const categoryGrid = root.querySelector('[data-category-grid]');
    const departureBoard = root.querySelector('[data-collection-departures]');
    const studioTransit = root.querySelector('[data-studio-transit]');
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const revealCollections = () => {
      categoryGrid?.classList.add('is-arrived');
      departureBoard?.classList.add('is-arrived');
    };

    if (categoryGrid && 'IntersectionObserver' in window && !reduceMotion) {
      categoryGrid.classList.add('chr-motion-ready');
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        revealCollections();
        observer.disconnect();
      }, { threshold: .16, rootMargin: '0px 0px -8% 0px' });
      observer.observe(categoryGrid);
    } else {
      revealCollections();
    }

    const routeStations = [...root.querySelectorAll('[data-route-station]')];
    const routePopover = root.querySelector('[data-route-popover]');
    const routePlate = root.querySelector('.hero-plate');
    let activeRouteStation = null;
    let routeCloseTimer = null;

    const routeFields = routePopover ? {
      number: routePopover.querySelector('[data-popover-number]'),
      type: routePopover.querySelector('[data-popover-type]'),
      status: routePopover.querySelector('[data-popover-status]'),
      title: routePopover.querySelector('[data-popover-title]'),
      meta: routePopover.querySelector('[data-popover-meta]'),
      link: routePopover.querySelector('[data-popover-link]'),
      nodes: [...routePopover.querySelectorAll('.route-station-popover__network span')],
    } : null;

    const positionRoutePopover = (station) => {
      if (!routePopover || !routePlate || !station || window.matchMedia('(max-width: 650px)').matches) return;
      const stationRect = station.getBoundingClientRect();
      const plateRect = routePlate.getBoundingClientRect();
      const popoverRect = routePopover.getBoundingClientRect();
      const stationCenterX = stationRect.left - plateRect.left + (stationRect.width / 2);
      const stationCenterY = stationRect.top - plateRect.top + (stationRect.height / 2);
      const openRight = stationCenterX < plateRect.width * .58;
      let left = openRight ? stationCenterX + 21 : stationCenterX - popoverRect.width - 21;
      let top = stationCenterY - (popoverRect.height / 2);
      left = Math.max(14, Math.min(left, plateRect.width - popoverRect.width - 14));
      top = Math.max(14, Math.min(top, plateRect.height - popoverRect.height - 14));
      routePopover.style.left = `${left}px`;
      routePopover.style.right = 'auto';
      routePopover.style.top = `${top}px`;
      routePopover.style.bottom = 'auto';
      routePopover.style.setProperty('--route-popover-origin', openRight ? '0 50%' : '100% 50%');
    };

    const openRouteStation = (station) => {
      if (!routePopover || !routeFields || !station) return;
      window.clearTimeout(routeCloseTimer);
      activeRouteStation?.classList.remove('is-active');
      activeRouteStation = station;
      activeRouteStation.classList.add('is-active');
      const index = routeStations.indexOf(station);
      routeFields.number.textContent = station.dataset.stationNumber || String(index + 1).padStart(2, '0');
      routeFields.type.textContent = station.dataset.stationType || 'CHROMATICA ROUTE';
      routeFields.status.textContent = station.dataset.stationStatus || 'OPEN ROUTE';
      routeFields.title.textContent = station.dataset.stationTitle || station.getAttribute('aria-label') || 'CHROMATICA';
      routeFields.meta.textContent = station.dataset.stationMeta || 'ARCHIVE / TRANSIT / LIVE';
      routeFields.link.href = station.href;
      routeFields.nodes.forEach((node, nodeIndex) => node.classList.toggle('is-active', nodeIndex === index));
      routePopover.hidden = false;
      requestAnimationFrame(() => {
        positionRoutePopover(station);
        requestAnimationFrame(() => routePopover.classList.add('is-open'));
      });
    };

    const closeRouteStation = (delay = 0) => {
      if (!routePopover) return;
      window.clearTimeout(routeCloseTimer);
      routeCloseTimer = window.setTimeout(() => {
        routePopover.classList.remove('is-open');
        activeRouteStation?.classList.remove('is-active');
        activeRouteStation = null;
        window.setTimeout(() => { if (!routePopover.classList.contains('is-open')) routePopover.hidden = true; }, 190);
      }, delay);
    };

    routeStations.forEach((station) => {
      station.addEventListener('pointerenter', (event) => { if (event.pointerType !== 'touch') openRouteStation(station); });
      station.addEventListener('pointerleave', (event) => { if (event.pointerType !== 'touch') closeRouteStation(130); });
      station.addEventListener('focus', () => openRouteStation(station));
      station.addEventListener('blur', (event) => { if (!routePopover?.contains(event.relatedTarget)) closeRouteStation(90); });
      station.addEventListener('click', (event) => {
        const coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        if (coarse && activeRouteStation !== station) {
          event.preventDefault();
          openRouteStation(station);
        }
      });
    });
    routePopover?.addEventListener('pointerenter', () => window.clearTimeout(routeCloseTimer));
    routePopover?.addEventListener('pointerleave', () => closeRouteStation(120));
    routePopover?.addEventListener('focusout', (event) => {
      if (!routePopover.contains(event.relatedTarget) && !activeRouteStation?.contains?.(event.relatedTarget)) closeRouteStation(90);
    });
    document.addEventListener('pointerdown', (event) => {
      if (!activeRouteStation || routePopover?.contains(event.target) || activeRouteStation.contains(event.target)) return;
      closeRouteStation();
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeRouteStation(); });
    window.addEventListener('resize', () => { if (activeRouteStation) positionRoutePopover(activeRouteStation); }, { passive: true });

    const revealStudio = () => studioTransit?.classList.add('is-studio-arrived');

    if (studioTransit && 'IntersectionObserver' in window && !reduceMotion) {
      studioTransit.classList.add('chr-studio-motion-ready');
      const studioObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        revealStudio();
        studioObserver.disconnect();
      }, { threshold: .18, rootMargin: '0px 0px -6% 0px' });
      studioObserver.observe(studioTransit);
    } else {
      revealStudio();
    }
  };
  const initAll = (scope = document) => scope.querySelectorAll('.archive-site').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => initAll()) : initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();


/* CHR / HOMEPAGE V3 / OBJECT ROTATION */
(() => {
  const roots = document.querySelectorAll('[data-hero-rotation]');
  roots.forEach((root) => {
    const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
    const prev = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    const counter = root.querySelector('[data-hero-counter]');
    if (slides.length < 2) return;

    let current = 0;
    let timer = null;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const show = (index, userInitiated = false) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === current;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        const active = i === current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (counter) counter.textContent = 'OBJECT ' + String(current + 1).padStart(2, '0') + ' / ' + String(slides.length).padStart(2, '0');
      if (userInitiated) stop();
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      if (reduced || timer) return;
      timer = window.setInterval(() => show(current + 1), 8000);
    };

    dots.forEach((dot, i) => dot.addEventListener('click', () => show(i, true)));
    if (prev) prev.addEventListener('click', () => show(current - 1, true));
    if (next) next.addEventListener('click', () => show(current + 1, true));

    let touchX = null;
    root.addEventListener('touchstart', (event) => { touchX = event.touches[0]?.clientX ?? null; }, { passive: true });
    root.addEventListener('touchend', (event) => {
      if (touchX == null) return;
      const endX = event.changedTouches[0]?.clientX ?? touchX;
      const delta = endX - touchX;
      touchX = null;
      if (Math.abs(delta) > 45) show(current + (delta < 0 ? 1 : -1), true);
    }, { passive: true });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());

    show(0);
    start();
  });
})();
